import { SubscriptionType } from 'store/billing/models/subscriptionType';

type LoosePlan = Record<string, unknown>;

const PLAN_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isPlanUuid = (value: string): boolean => PLAN_UUID_RE.test(value);

const planId = (plan: LoosePlan): string =>
  String(
    plan.uuid ??
      plan.UUID ??
      plan.Uuid ??
      plan.id ??
      plan.Id ??
      plan.typeId ??
      plan.TypeId ??
      plan.licenseTypeId ??
      plan.LicenseTypeId ??
      plan.planUuid ??
      plan.PlanUuid ??
      ''
  );

const planLabel = (plan: LoosePlan): string =>
  String(
    plan.name ??
      plan.Name ??
      plan.displayName ??
      plan.DisplayName ??
      plan.planName ??
      plan.PlanName ??
      plan.description ??
      plan.Description ??
      ''
  );

export const normalizeSubscriptionPlanType = (
  raw: unknown
): SubscriptionType | null => {
  if (!raw || typeof raw !== 'object') return null;
  const plan = raw as LoosePlan;
  const uuid = planId(plan);
  const name = planLabel(plan);
  if (!uuid && !name) return null;
  return {
    ...(plan as unknown as SubscriptionType),
    uuid,
    name,
    displayName: String(plan.displayName ?? plan.DisplayName ?? name),
  };
};

const isPlanArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) &&
  value.some(
    (item) =>
      item &&
      typeof item === 'object' &&
      (planId(item as LoosePlan) || planLabel(item as LoosePlan))
  );

const toPlanList = (value: unknown): unknown[] | null => {
  if (isPlanArray(value)) return value;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const values = Object.values(value as LoosePlan);
    if (isPlanArray(values)) return values;
  }
  return null;
};

const toNormalizedPlans = (value: unknown): SubscriptionType[] => {
  const list = toPlanList(value);
  if (!list) return [];
  return list
    .map(normalizeSubscriptionPlanType)
    .filter((plan): plan is SubscriptionType => Boolean(plan?.uuid));
};

/** Pull license types from the shapes GT and the .NET API actually return. */
export const extractLicenseTypes = (response: unknown): SubscriptionType[] => {
  if (!response || typeof response !== 'object') return [];
  const root = response as LoosePlan;
  const nested =
    root.data && typeof root.data === 'object'
      ? (root.data as LoosePlan)
      : undefined;

  const candidates: unknown[] = [
    nested?.licenseTypes,
    nested?.LicenseTypes,
    nested?.plans,
    nested,
    root.licenseTypes,
    root.LicenseTypes,
    root,
  ];

  for (const candidate of candidates) {
    const plans = toNormalizedPlans(candidate);
    if (plans.length) return plans;
  }

  for (const bucket of [nested, root]) {
    if (!bucket || typeof bucket !== 'object') continue;
    for (const value of Object.values(bucket)) {
      const plans = toNormalizedPlans(value);
      if (plans.length) return plans;
    }
  }
  return [];
};

export const FEATURES_PLAN_KEYS = [
  'small',
  'medium',
  'large',
  'enterprise',
  'free',
] as const;

export type FeaturesPlanKey = (typeof FEATURES_PLAN_KEYS)[number];

/** Map API names like SmallRows / Small Plan to features.json keys. */
export const toFeaturesPlanKey = (planName?: string): string => {
  if (!planName) return '';
  const lower = planName.toLowerCase().trim();
  const first = lower.split(/[\s_-]+/)[0] ?? '';
  for (const key of FEATURES_PLAN_KEYS) {
    if (first === key || lower === key || lower.startsWith(key)) return key;
  }
  return first;
};

/** Match Activate lookup: first word of Small / Medium / Large against API name. */
export const findSubscriptionPlanType = (
  plans: SubscriptionType[],
  planName: string
): SubscriptionType | undefined => {
  const needle = planName.split(/[\s_-]+/)[0]?.toLowerCase() ?? '';
  if (!needle) return undefined;

  const exact = plans.find((plan) => {
    const name = (plan.name || plan.displayName || '').toLowerCase();
    return name === planName.toLowerCase() || name === needle;
  });
  if (exact) return exact;

  return plans.find((plan) => {
    const name = (plan.name || plan.displayName || '').toLowerCase();
    if (!name) return false;
    const compact = name.replace(/[^a-z0-9]/g, '');
    return (
      name.split(/[\s_-]+/)[0] === needle ||
      compact === needle ||
      compact.startsWith(needle) ||
      name.includes(needle) ||
      compact.includes(needle)
    );
  });
};
